import React, { useState, useEffect } from 'react';
import {
  Wrench, Plus, Calendar, Search, X, ChevronDown, ChevronUp,
  Trash2, Edit, FileText, AlertTriangle, CheckCircle, Clock, List, Upload, Download, Paperclip
} from 'lucide-react';
import { getMaintenanceInterventions, getMaintenanceMultiStructures, getMaintenanceTypes, createMaintenanceIntervention, updateMaintenanceIntervention, deleteMaintenanceIntervention, uploadMaintenanceFile, deleteMaintenanceFile } from '../api';
import { useStructure } from '../contexts/StructureContext';
import MaintenanceCard from '../components/maintenance/MaintenanceCard';
import LoadingSpinner from '../components/LoadingSpinner';
import SignatureCanvas from '../components/maintenance/SignatureCanvas';
import { useToast } from '../contexts/NotificationContext';

const TYPE_ICONS = {
  'Climatisation / Chauffage': '🌡️',
  'Extincteurs': '🔥',
  'VMC / Ventilation': '💨',
  'Dératisation / Désinsectisation': '🐛',
  'Contrôle électrique': '⚡',
  'Analyse d\'eau': '💧',
  'Prélèvements de surface': '🧪',
  'Contrôle sanitaire général': '📋',
};

const MaintenancePage = ({ showAllStructures = false }) => {
  const { currentStructure, structures } = useStructure();
  const toast = useToast();
  const [interventions, setInterventions] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // cards, history
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingIntervention, setEditingIntervention] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [selectedIntervention, setSelectedIntervention] = useState(null);

  const [form, setForm] = useState({
    typeId: '', date: new Date().toISOString().split('T')[0],
    company: '', technicianName: '', description: '',
    cost: '', status: 'completed', notes: '', signatureData: null, structureId: '',
  });

  const isMulti = showAllStructures && structures.length >= 2;

  useEffect(() => {
    if (currentStructure) {
      fetchData();
    }
  }, [currentStructure, showAllStructures]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const structureIds = structures.map(s => s.id);
      const [intRes, typeRes] = await Promise.all([
        isMulti
          ? getMaintenanceMultiStructures(structureIds)
          : getMaintenanceInterventions(currentStructure.id),
        getMaintenanceTypes(),
      ]);
      setInterventions(intRes.data);
      setTypes(typeRes.data);
    } catch (error) {
      console.error('Error fetching maintenance data:', error);
      toast.error('Erreur lors du chargement des interventions');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      typeId: '', date: new Date().toISOString().split('T')[0],
      company: '', technicianName: '', description: '',
      cost: '', status: 'completed', notes: '', signatureData: null, structureId: '',
    });
    setEditingIntervention(null);
    setShowAddForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.typeId || !form.company.trim()) return;

    setSaving(true);
    try {
      const data = {
        ...form,
        structureId: form.structureId || currentStructure.id,
        cost: form.cost ? parseFloat(form.cost) : null,
      };

      if (editingIntervention) {
        await updateMaintenanceIntervention(editingIntervention.id, data);
        toast.success('Intervention modifiee');
      } else {
        await createMaintenanceIntervention(data);
        toast.success('Intervention ajoutee');
      }
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving intervention:', error);
      toast.error('Erreur lors de la sauvegarde de l\'intervention');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (intervention) => {
    setForm({
      typeId: intervention.typeId || '',
      date: intervention.date ? intervention.date.split('T')[0] : '',
      company: intervention.company || '',
      technicianName: intervention.technicianName || '',
      description: intervention.description || '',
      cost: intervention.cost || '',
      status: intervention.status || 'completed',
      notes: intervention.notes || '',
      signatureData: intervention.signatureData || null,
      structureId: intervention.structureId || '',
    });
    setEditingIntervention(intervention);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette intervention ?')) return;
    try {
      await deleteMaintenanceIntervention(id);
      toast.success('Intervention supprimee');
      fetchData();
    } catch (error) {
      console.error('Error deleting intervention:', error);
      toast.error('Erreur lors de la suppression de l\'intervention');
    }
  };

  const openAddForType = (type) => {
    resetForm();
    setForm((prev) => ({ ...prev, typeId: type.id }));
    setShowAddForm(true);
  };

  // Group last intervention per type for cards view
  const lastInterventionByType = {};
  interventions.forEach((int) => {
    if (!lastInterventionByType[int.typeId] ||
      new Date(int.date) > new Date(lastInterventionByType[int.typeId].date)) {
      lastInterventionByType[int.typeId] = int;
    }
  });

  // Count overdue
  const now = new Date();
  const overdueCount = Object.values(lastInterventionByType).filter(
    (int) => int.nextDueDate && new Date(int.nextDueDate) < now
  ).length;

  // Filtered history
  const filteredHistory = interventions.filter((int) => {
    if (filterType !== 'all' && int.typeId !== filterType) return false;
    if (searchText) {
      const s = searchText.toLowerCase();
      return (
        int.company.toLowerCase().includes(s) ||
        (int.technicianName && int.technicianName.toLowerCase().includes(s)) ||
        (int.description && int.description.toLowerCase().includes(s)) ||
        (int.type?.name && int.type.name.toLowerCase().includes(s))
      );
    }
    return true;
  });

  if (!currentStructure) {
    return (
      <div className="p-8 text-center text-gray-500">
        Sélectionnez une structure pour voir la maintenance.
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      {/* Overdue alert */}
      {overdueCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
          <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="font-semibold text-red-800">
              {overdueCount} type(s) de maintenance en retard
            </p>
            <p className="text-sm text-red-700 mt-1">
              Des interventions sont en attente. Planifiez-les dès que possible.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Wrench className="text-primary" size={24} />
          <h2 className="text-xl font-bold text-gray-800">Maintenance & Entretien</h2>
          <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">
            {interventions.length} interventions
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('cards')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition ${
                viewMode === 'cards' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500'
              }`}
            >
              <Calendar size={14} />
              Planning
            </button>
            <button
              onClick={() => setViewMode('history')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition ${
                viewMode === 'history' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500'
              }`}
            >
              <List size={14} />
              Historique
            </button>
          </div>
        </div>
      </div>

      {/* Add/Edit form */}
      {showAddForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-4 mb-4 border border-primary/20">
          <h3 className="font-semibold text-gray-800 mb-3">
            {editingIntervention ? 'Modifier l\'intervention' : 'Ajouter une intervention'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {isMulti && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Structure *</label>
                <select value={form.structureId} onChange={(e) => setForm({ ...form, structureId: e.target.value })} required
                  className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none">
                  <option value="">Sélectionner une structure</option>
                  {structures.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
              <select value={form.typeId} onChange={(e) => setForm({ ...form, typeId: e.target.value })} required
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none">
                <option value="">Sélectionner un type...</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise *</label>
              <input type="text" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} required
                placeholder="Nom de l'entreprise"
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Technicien</label>
              <input type="text" value={form.technicianName} onChange={(e) => setForm({ ...form, technicianName: e.target.value })}
                placeholder="Nom du technicien"
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Coût (€)</label>
              <input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none">
                <option value="completed">Réalisée</option>
                <option value="scheduled">Planifiée</option>
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description / Notes</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2} placeholder="Détails de l'intervention..."
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none resize-none" />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <SignatureCanvas
                onSave={(data) => setForm({ ...form, signatureData: data })}
                initialData={form.signatureData}
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <button type="submit" disabled={saving} className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? 'Enregistrement...' : (editingIntervention ? 'Enregistrer' : 'Ajouter')}
            </button>
            <button type="button" onClick={resetForm} className="text-gray-500 hover:text-gray-700 px-4 py-2 text-sm">
              Annuler
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="py-12"><LoadingSpinner size="large" text="Chargement..." /></div>
      ) : viewMode === 'cards' ? (
        /* ===== CARDS / PLANNING VIEW ===== */
        types.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
            <Wrench size={48} className="mx-auto mb-3 text-gray-300" />
            <p>Aucun type de maintenance configuré</p>
            <p className="text-sm mt-1">Contactez un administrateur pour ajouter les types de maintenance.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {types.map((type) => (
              <MaintenanceCard
                key={type.id}
                type={{ ...type, icon: TYPE_ICONS[type.name] || '🔧' }}
                lastIntervention={lastInterventionByType[type.id] || null}
                onAddIntervention={openAddForType}
              />
            ))}
          </div>
        )
      ) : (
        /* ===== HISTORY VIEW ===== */
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Rechercher par entreprise, technicien..."
                value={searchText} onChange={(e) => setSearchText(e.target.value)}
                className="w-full border border-gray-300 rounded-lg py-2.5 pl-10 pr-9 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none bg-white" />
              {searchText && (
                <button onClick={() => setSearchText('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X size={16} />
                </button>
              )}
            </div>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
              className="border border-gray-300 rounded-lg py-2.5 px-3 text-sm bg-white focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none min-w-[180px]">
              <option value="all">Tous les types</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          {filteredHistory.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
              <p>Aucune intervention enregistrée</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 text-left text-sm text-gray-600">
                      <th className="px-4 py-3 font-medium">Type</th>
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium">Entreprise</th>
                      <th className="px-4 py-3 font-medium hidden md:table-cell">Technicien</th>
                      <th className="px-4 py-3 font-medium hidden lg:table-cell">Coût</th>
                      <th className="px-4 py-3 font-medium hidden lg:table-cell">Prochain</th>
                      <th className="px-4 py-3 font-medium hidden sm:table-cell text-center">Docs</th>
                      <th className="px-4 py-3 font-medium w-24"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredHistory.map((int) => {
                      const isOverdue = int.nextDueDate && new Date(int.nextDueDate) < now;
                      return (
                        <tr key={int.id} className="text-sm hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span className="flex items-center gap-2">
                              <span>{TYPE_ICONS[int.type?.name] || '🔧'}</span>
                              <span className="font-medium">{int.type?.name || 'N/A'}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3">{new Date(int.date).toLocaleDateString('fr-FR')}</td>
                          <td className="px-4 py-3">
                            <span>{int.company}</span>
                            {isMulti && int.structureName && (
                              <span className="ml-2 bg-primary/10 text-primary text-xs font-medium px-1.5 py-0.5 rounded">{int.structureName}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell text-gray-500">{int.technicianName || '—'}</td>
                          <td className="px-4 py-3 hidden lg:table-cell text-gray-500">
                            {int.cost ? `${parseFloat(int.cost).toFixed(2)}€` : '—'}
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            {int.nextDueDate ? (
                              <span className={isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}>
                                {new Date(int.nextDueDate).toLocaleDateString('fr-FR')}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell text-center">
                            <div className="flex justify-center gap-1">
                              {int.invoiceUrl && <Paperclip size={14} className="text-primary" title="Facture" />}
                              {int.reportUrl && <FileText size={14} className="text-primary" title="Compte rendu" />}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button onClick={() => setSelectedIntervention(int)} className="p-1 text-gray-400 hover:text-primary transition" title="Détails / Documents">
                                <FileText size={16} />
                              </button>
                              <button onClick={() => handleEdit(int)} className="p-1 text-gray-400 hover:text-blue-500 transition" title="Modifier">
                                <Edit size={16} />
                              </button>
                              <button onClick={() => handleDelete(int.id)} className="p-1 text-gray-400 hover:text-red-500 transition" title="Supprimer">
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Intervention detail panel (documents) */}
      {selectedIntervention && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setSelectedIntervention(null)} />
          <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-xl z-50 overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-bold text-gray-800">
                {selectedIntervention.type?.name || 'Intervention'}
              </h3>
              <button onClick={() => setSelectedIntervention(null)} className="p-1 text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-500">Date :</span> {new Date(selectedIntervention.date).toLocaleDateString('fr-FR')}</p>
                <p><span className="text-gray-500">Entreprise :</span> {selectedIntervention.company}</p>
                {selectedIntervention.technicianName && <p><span className="text-gray-500">Technicien :</span> {selectedIntervention.technicianName}</p>}
                {selectedIntervention.cost && <p><span className="text-gray-500">Coût :</span> {parseFloat(selectedIntervention.cost).toFixed(2)}€</p>}
                {selectedIntervention.description && <p><span className="text-gray-500">Description :</span> {selectedIntervention.description}</p>}
                {selectedIntervention.nextDueDate && <p><span className="text-gray-500">Prochain :</span> {new Date(selectedIntervention.nextDueDate).toLocaleDateString('fr-FR')}</p>}
              </div>

              {/* Documents */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-700 flex items-center gap-2 mb-3">
                  <FileText size={16} />
                  Documents
                </h4>
                <div className="space-y-2">
                  {[
                    { type: 'invoice', label: 'Facture', url: selectedIntervention.invoiceUrl, name: selectedIntervention.invoiceName },
                    { type: 'report', label: 'Compte rendu', url: selectedIntervention.reportUrl, name: selectedIntervention.reportName },
                  ].map(({ type, label, url, name }) => (
                    <div key={type} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                      <span className="text-sm text-gray-600">{label}</span>
                      {url ? (
                        <div className="flex items-center gap-2">
                          <a href={url} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-primary hover:underline font-medium">
                            <Download size={14} />
                            {name || 'Télécharger'}
                          </a>
                          <button
                            onClick={async () => {
                              try {
                                await deleteMaintenanceFile(selectedIntervention.id, type);
                                fetchData();
                                setSelectedIntervention(prev => ({
                                  ...prev,
                                  [`${type}Url`]: null,
                                  [`${type}Name`]: null,
                                }));
                              } catch (error) {
                                console.error('Error deleting file:', error);
                                toast.error('Erreur lors de la suppression du document');
                              }
                            }}
                            className="p-1 text-red-400 hover:text-red-600 transition" title="Supprimer"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-1 text-xs text-primary cursor-pointer hover:underline font-medium">
                          <Upload size={14} />
                          Ajouter
                          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                            onChange={async (e) => {
                              if (!e.target.files[0]) return;
                              try {
                                const res = await uploadMaintenanceFile(selectedIntervention.id, type, e.target.files[0]);
                                toast.success('Document uploade');
                                fetchData();
                                setSelectedIntervention(prev => ({
                                  ...prev,
                                  [`${type}Url`]: res.data[`${type}Url`],
                                  [`${type}Name`]: res.data[`${type}Name`],
                                }));
                              } catch (error) {
                                console.error('Error uploading file:', error);
                                toast.error('Erreur lors de l\'upload du document');
                              }
                            }}
                          />
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Signature */}
              {selectedIntervention.signatureData && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Signature</h4>
                  <img
                    src={selectedIntervention.signatureData}
                    alt="Signature"
                    className="border border-gray-200 rounded-lg max-w-full"
                    style={{ maxHeight: '120px' }}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* FAB — Nouvelle intervention */}
      <button
        onClick={() => { resetForm(); setShowAddForm(true); }}
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 bg-primary text-white rounded-full shadow-lg hover:bg-primary-dark transition-all hover:scale-110"
        title="Nouvelle intervention"
      >
        <Plus size={26} />
      </button>
    </div>
  );
};

export default MaintenancePage;
